// src/redux/IntegrationSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import IntegrationService from "../../services/api/integrationService";
import { toast } from "react-toastify";
import {
  setIsConfirmation,
  setIsSecondSubmittion,
  setSessionExpired,
} from "./globalSlice";
import { unauthorizedError } from "../../utils/helpers";
import { setIsPopUpShow } from "./popUpSlice";

const integration = new IntegrationService();

export const getIntegrationsData = createAsyncThunk(
  "/integration",
  async (navigate, { dispatch }) => {
    try {
      const response = integration.getIntegrationData(navigate);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const getOrgIntegrationsData = createAsyncThunk(
  "/orgIntegration",
  async (navigate, { dispatch }) => {
    try {
      const response = integration.getOrgIntegrationData(navigate);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const removeIntegrationsData = createAsyncThunk(
  "/integration/default",
  async ({ name, navigate }, { dispatch }) => {
    try {
      const response = await integration.removeIntegration(name, navigate);
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response?.message || "App deactivated successfully");
      dispatch(setIsSecondSubmittion(false));
      return response;
    } catch (error) {
      dispatch(setIsSecondSubmittion(false));
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
        dispatch(setIsSecondSubmittion(false));
      }
      throw error;
    }
  },
);

//update default integration
export const updateIntegrationsData = createAsyncThunk(
  "/integration/default",
  async (body, navigate, { dispatch }) => {
    try {
      const response = await integration.updateIntegration(body, navigate);
      toast.success(response.message);
      dispatch(getIntegrationsData());
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const updateJiraProject = createAsyncThunk(
  "/integration/jira",
  async ({ projectName, navigate }, { dispatch }) => {
    try {
      const response = await integration.updateJiraProject(
        projectName,
        navigate,
      );
      toast.success(response.message);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

//keka api integration
export const kekaIntegrations = createAsyncThunk(
  "/integration/keka",
  async ({ body, navigate }, { dispatch }) => {
    try {
      const response = await integration.sendKekaIntegration(body, navigate);
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response.message);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message,
        );
      }

      throw error?.data?.message;
    }
  },
);
//telegram api integration
export const telegramIntegrations = createAsyncThunk(
  "/integration/telegram",
  async ({ body, navigate }, { dispatch }) => {
    try {
      const response = await integration.sendTelegramIntegration(
        body,
        navigate,
      );
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response?.message);
      dispatch(setIsTelegramActivate(response?.data));
      if (response?.data) {
        dispatch(setOepnTelegram(true));
      }
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message,
        );
      }
      throw error?.data?.message;
    }
  },
);

//dbf api integration
export const dbfIntegrations = createAsyncThunk(
  "/integration/dbf",
  async ({ body, navigate }, { dispatch }) => {
    try {
      const response = await integration.sendDBFIntegration(body, navigate);
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response?.message || response.message);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message,
        );
      }
      throw error?.data?.message;
    }
  },
);

//thunk for DBF Google SSO
export const dbfGoogleSSO = createAsyncThunk(
  "/integration/dbf/sso",
  async (navigate, { dispatch }) => {
    try {
      // This will redirect the user; no response expected here
      await integration.sendDBFGoogleSSO(navigate);
      // Note: Since it's a redirect, the thunk may not complete normally.
      // Handle success in the callback endpoint on the backend.
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors
            ? error?.data?.errors
            : error?.data?.message || "SSO failed",
        );
      }
      throw error?.data?.message;
    }
  },
);

//odoo api integration
export const odooIntegrations = createAsyncThunk(
  "/integration/odoo/password-login",
  async ({ body, navigate }, { dispatch }) => {
    try {
      const response = await integration.sendOdooIntegration(body, navigate);
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response?.message || response.message);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message,
        );
      }
      throw error?.data?.message;
    }
  },
);

//active api integration
export const activateIntegrations = createAsyncThunk(
  "/activation",
  async ({ body, navigate }, { dispatch }) => {
    try {
      const response = await integration.activateIntegration(body, navigate);
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response?.message);
      dispatch(setIsConfirmation(false));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message,
        );
      }
      throw error?.data?.message;
    }
  },
);

//active api integration telegram
export const activateIntegrationsTelegram = createAsyncThunk(
  "/app/activation/telegram",
  async ({ body, navigate }, { dispatch }) => {
    try {
      const response = await integration.activateIntegrationTelegram(
        body,
        navigate,
      );
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response?.message);
      dispatch(setTelegramToken(""));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else if (error?.response) {
        toast.error(error?.message);
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message,
        );
      }
      throw error?.data?.message;
    }
  },
);

//acivate whatsapp
export const activateWhatsapp = createAsyncThunk(
  "/admin/whatsapp",
  async ({ body, navigate, organizationId }, { dispatch }) => {
    try {
      const response = await integration.activatetWhatsappIntegration(
        body,
        navigate,
        organizationId,
      );
      await dispatch(getIntegrationsData(navigate));
      await dispatch(getYourAppsData(navigate));
      await dispatch(getOrgIntegrationsData(navigate));
      toast.success(response?.message);
      dispatch(setVerifyToken(""));
      dispatch(setAccessToken(""));
      dispatch(setPhoneNumberId(""));
      dispatch(setBusinessAccountId(""));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else if (error?.response) {
        toast.error(error?.message);
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message,
        );
      }
      throw error?.data?.message;
    }
  },
);

//darwinbox integration api
export const getDarwinBox = createAsyncThunk(
  "/integration/darwinbox",
  async ({ employeeId, body, navigate }, { dispatch }) => {
    try {
      const response = await integration.getDawrwinBoxApi(
        employeeId,
        body,
        navigate,
      );
      dispatch(getIntegrationsData());
      toast.success(response.message);
      dispatch(setIsPopUpShow(false));
      dispatch(setIsConfirmation(false));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.error ? error?.data?.error : error?.data?.message,
        );
      }
      throw error;
    }
  },
);
//whatsapp integration api (admin: phone number only)
export const whatsappIntegration = createAsyncThunk(
  "/integration/whatsapp",
  async ({ body, navigate }, { dispatch }) => {
    try {
      const response = await integration.whatsappIntegrationApi(body, navigate);
      dispatch(getIntegrationsData(navigate));
      dispatch(getYourAppsData(navigate));
      toast.success(response.message);
      return response;
    } catch (error) {
      toast.error(error?.data?.message ?? "Oops somthing went wrong !!");
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data
            ? error?.data
            : (error?.data?.message ?? "Oops somthing went wrong !!"),
        );
      }
      throw error;
    }
  },
);

export const getYourAppsData = createAsyncThunk(
  "/admin/apps",
  async (navigate, { dispatch }) => {
    try {
      const response = await integration.getYourApps(navigate);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const getAdminAppsMarketplace = createAsyncThunk(
  "/admin/apps-marketplace",
  async (navigate, { dispatch }) => {
    try {
      const response = await integration.getAdminAppsMarketplace(navigate);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const requestAppsMarketplaceEnable = createAsyncThunk(
  "/admin/apps-marketplace-request",
  async ({ appId, navigate }, { dispatch }) => {
    try {
      const response = await integration.requestAppsMarketplaceEnable(
        { appId },
        navigate,
      );
      await dispatch(getAdminAppsMarketplace(navigate));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

const IntegrationSlice = createSlice({
  name: "Integration",
  initialState: {
    loader: false,
    isYourAppsLoading: false,
    isJiraProjectsLoading: false,
    isTelegramActivate: "",
    openTelegram: false,
    refreshToken: "",
    employeeId: "",
    employeeNo: "",
    user: {
      email: "",
      password: "",
    },
    telegramId: "",
    telegramToken: "",
    verifyToken: "",
    integrationDrawerOpen: false,
    whatsappNumber: "",
    whatsappCountryCode: null,
    accessToken: "",
    phoneNumberId: "",
    businessAccountId: "",
    yourAppsList: [],
    adminAppsMarketplace: null,
    requestAppLoadingId: null,
  },
  reducers: {
    setIsTelegramActivate: (state, action) => {
      state.isTelegramActivate = action.payload;
    },
    setOepnTelegram: (state, action) => {
      state.openTelegram = action.payload;
    },
    setIsDarwinBox: (state, action) => {
      state.isDarwinbox = action.payload;
      state.isDBF = false;
      state.isKeka = false;
      state.isTelegram = false;
    },
    setRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
    },
    setEmployeeId: (state, action) => {
      state.employeeId = action.payload;
    },
    setEmployeeNo: (state, action) => {
      state.employeeNo = action.payload;
    },
    setUser: (state, action) => {
      const { email, password } = action.payload;
      if (email !== undefined) state.user.email = email;
      if (password !== undefined) state.user.password = password;
    },
    setTelegramId: (state, action) => {
      state.telegramId = action.payload;
    },
    setTelegramToken: (state, action) => {
      state.telegramToken = action.payload;
    },
    setVerifyToken: (state, action) => {
      state.verifyToken = action.payload;
    },
    setIntegrationDrawerOpen: (state, action) => {
      state.integrationDrawerOpen = action.payload;
    },
    setWhatsappNumber: (state, action) => {
      state.whatsappNumber = action.payload;
    },
    setWhatsappCountryCode: (state, action) => {
      state.whatsappCountryCode = action.payload;
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    setPhoneNumberId: (state, action) => {
      state.phoneNumberId = action.payload;
    },
    setBusinessAccountId: (state, action) => {
      state.businessAccountId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getIntegrationsData.pending, (state) => {
        state.isYourAppsLoading = true;
      })
      .addCase(getIntegrationsData.fulfilled, (state, action) => {
        state.isYourAppsLoading = false;
        const raw = action.payload;
        const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
        state.integrationList = list.map((item) => ({
          ...item,
          isActivated: Boolean(
            item.isActivated ?? item.activated ?? item.isActive ?? false,
          ),
        }));
      })
      .addCase(getIntegrationsData.rejected, (state) => {
        state.isYourAppsLoading = false;
      })
      .addCase(updateIntegrationsData.pending, (state) => {
        state.loader = true;
      })
      .addCase(updateIntegrationsData.fulfilled, (state, action) => {
        state.loader = false;
      })

      .addCase(updateJiraProject.pending, (state) => {
        state.isJiraProjectsLoading = true;
      })
      .addCase(updateJiraProject.fulfilled, (state, action) => {
        state.isJiraProjectsLoading = false;
      })
      .addCase(updateJiraProject.rejected, (state) => {
        state.isJiraProjectsLoading = false;
      })

      .addCase(updateIntegrationsData.rejected, (state) => {
        state.loader = false;
      })
      .addCase(kekaIntegrations.pending, (state) => {
        state.loader = true;
      })
      .addCase(kekaIntegrations.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(kekaIntegrations.rejected, (state) => {
        state.loader = false;
      })
      .addCase(telegramIntegrations.pending, (state) => {
        state.loader = true;
      })
      .addCase(telegramIntegrations.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(telegramIntegrations.rejected, (state) => {
        state.loader = false;
      })
      .addCase(dbfIntegrations.pending, (state) => {
        state.loader = true;
      })
      .addCase(dbfIntegrations.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(dbfIntegrations.rejected, (state) => {
        state.loader = false;
      })
      .addCase(odooIntegrations.pending, (state) => {
        state.loader = true;
      })
      .addCase(odooIntegrations.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(odooIntegrations.rejected, (state) => {
        state.loader = false;
      })
      .addCase(getOrgIntegrationsData.pending, (state) => {
        state.isYourAppsLoading = true;
      })
      .addCase(getOrgIntegrationsData.fulfilled, (state, action) => {
        state.isYourAppsLoading = false;
        const raw = action.payload;
        const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
        state.orgIntegrationList = list.map((item) => ({
          ...item,
          isActivated: Boolean(
            item.isActivated ?? item.activated ?? item.isActive ?? false,
          ),
        }));
      })
      .addCase(getOrgIntegrationsData.rejected, (state) => {
        state.isYourAppsLoading = false;
      })
      .addCase(activateIntegrations.pending, (state) => {
        state.loader = true;
      })
      .addCase(activateIntegrations.fulfilled, (state) => {
        state.loader = false;
        // List is updated by getYourAppsData / getOrgIntegrationsData in the thunk
      })
      .addCase(activateIntegrations.rejected, (state) => {
        state.loader = false;
      })
      .addCase(activateIntegrationsTelegram.pending, (state) => {
        state.loader = true;
      })
      .addCase(activateIntegrationsTelegram.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(activateIntegrationsTelegram.rejected, (state) => {
        state.loader = false;
      })
      .addCase(getDarwinBox.pending, (state) => {
        state.loader = true;
      })
      .addCase(getDarwinBox.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(getDarwinBox.rejected, (state) => {
        state.loader = false;
      })
      .addCase(whatsappIntegration.pending, (state) => {
        state.loader = true;
      })
      .addCase(whatsappIntegration.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(whatsappIntegration.rejected, (state) => {
        state.loader = false;
      })
      .addCase(activateWhatsapp.pending, (state) => {
        state.loader = true;
      })
      .addCase(activateWhatsapp.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(activateWhatsapp.rejected, (state) => {
        state.loader = false;
      })
      .addCase(getYourAppsData.pending, (state) => {
        state.isYourAppsLoading = true;
      })
      .addCase(getYourAppsData.fulfilled, (state, action) => {
        state.isYourAppsLoading = false;
        // Handle both array response and object with data property
        const responseData = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.data || [];

        // Parse actions if they're stringified JSON; normalize isActivated for UI
        const processedData = responseData.map((app) => {
          let actions = app.actions || [];

          if (Array.isArray(actions)) {
            actions = actions
              .map((action) => {
                if (
                  typeof action === "string" &&
                  action.startsWith("[") &&
                  action.endsWith("]")
                ) {
                  try {
                    const parsed = JSON.parse(action);
                    return Array.isArray(parsed) ? parsed : [parsed];
                  } catch (e) {
                    return action;
                  }
                }
                return action;
              })
              .flat();
          }

          // Normalize activation: API may return isActivated, activated, or isActive
          const isActivated =
            app.isActivated ?? app.activated ?? app.isActive ?? false;

          return {
            ...app,
            actions,
            isActivated: Boolean(isActivated),
          };
        });

        state.yourAppsList = processedData;
      })
      .addCase(getYourAppsData.rejected, (state) => {
        state.isYourAppsLoading = false;
      })
      .addCase(getAdminAppsMarketplace.pending, (state) => {
        state.loader = true;
      })
      .addCase(getAdminAppsMarketplace.fulfilled, (state, action) => {
        state.loader = false;
        state.adminAppsMarketplace =
          action.payload?.data ?? action.payload ?? null;
      })
      .addCase(getAdminAppsMarketplace.rejected, (state) => {
        state.loader = false;
      })
      .addCase(requestAppsMarketplaceEnable.pending, (state, action) => {
        state.requestAppLoadingId = action.meta.arg?.appId ?? null;
      })
      .addCase(requestAppsMarketplaceEnable.fulfilled, (state, action) => {
        state.requestAppLoadingId = null;
      })
      .addCase(requestAppsMarketplaceEnable.rejected, (state) => {
        state.requestAppLoadingId = null;
      });
  },
});

export const {
  setIsTelegramActivate,
  setOepnTelegram,
  setIsDarwinBox,
  setRefreshToken,
  setEmployeeId,
  setUser,
  setTelegramId,
  setTelegramToken,
  setIntegrationDrawerOpen,
  setWhatsappNumber,
  setWhatsappCountryCode,
  setEmployeeNo,
  setVerifyToken,
  setAccessToken,
  setPhoneNumberId,
  setBusinessAccountId,
} = IntegrationSlice.actions;

export default IntegrationSlice.reducer;
